import { GlobalStyles, Paper, Typography } from "@mui/material";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { useGridPanelFit } from "../hooks/useGridPanelFit";
import { mesfColors, mesfTheme } from "@/setup/ag-grid/mesf-theme";
import {
	EMPTY,
	type RollTurn,
	TURN_COLUMN_HELP,
} from "../models/RollTurnHistoryDetail";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";
const HEADER_HEIGHT = 28;
const ROW_HEIGHT = 26;

const turnHistoryTheme = mesfTheme.withParams({
	cellHorizontalPadding: 3,
	headerFontSize: 11,
	fontSize: 12,
});

const NO_ROWS_OVERLAY = `<span style="color:${mesfColors.text.secondary};font-size:13px">This roll has no turns on record</span>`;
const LOADING_OVERLAY = `<span style="color:${mesfColors.text.secondary};font-size:13px">Loading turns</span>`;

const dayOf = (value?: string | null) =>
	value ? String(value).slice(0, 10) : EMPTY;

const decimals = (params: ValueFormatterParams, places: number) =>
	params.value == null ? EMPTY : Number(params.value).toFixed(places);

const withTooltips = (defs: ColDef[]): ColDef[] =>
	defs.map((def) => {
		const text = def.field ? TURN_COLUMN_HELP[def.field] : undefined;
		if (!text) return def;
		return { ...def, headerTooltip: text, tooltipValueGetter: () => text };
	});

const RAW_COLUMN_DEFS: ColDef[] = [
	{
		field: "TimestampString",
		headerName: "Date Turned",
		flex: 1,
		minWidth: 92,
		maxWidth: 104,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => dayOf(p.value),
	},
	{
		field: "Diameter",
		headerName: "Turn Diameter",
		flex: 1,
		minWidth: 86,
		maxWidth: 96,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => decimals(p, 3),
	},
	{
		// The legacy header keeps this spelling; operators read the screen by it.
		field: "DiameterTurnedOff",
		headerName: "Amount Turned Off",
		flex: 1,
		minWidth: 96,
		maxWidth: 124,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => decimals(p, 3),
	},
	{
		field: "TimeToCut",
		headerName: "Turn Time",
		flex: 1,
		minWidth: 70,
		maxWidth: 82,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => decimals(p, 1),
	},
	{
		field: "Operator1Name",
		headerName: "Turn by Name 1",
		flex: 1,
		minWidth: 104,
		maxWidth: 134,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Operator2Name",
		headerName: "Turn by Name 2",
		flex: 1,
		minWidth: 104,
		maxWidth: 134,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Comments",
		headerName: "Turn Notes",
		// No cap: every pixel the capped columns give back lands here,
		// which is the column that actually needs the room.
		flex: 4,
		minWidth: 200,
		// The house grid theme sets wrapText with `white-space: normal !important`, so a
		// cellStyle cannot stop a long note from wrapping. Left as it wraps, and the row
		// grows to hold it: with a fixed row height the second line spills over the row
		// below.
		autoHeight: true,
		wrapText: true,
	},
];

interface Props {
	rows: RollTurn[];
	loading: boolean;
	/** Hands the grid up so the page prints the turns as the user sorted them. */
	onGridApi?: (api: GridApi) => void;
}

export const RollTurnHistoryGrid = ({ rows, loading, onGridApi }: Props) => {
	const { containerRef, apiRef, registerApi } = useGridPanelFit();
	const columnDefs = useMemo(() => withTooltips(RAW_COLUMN_DEFS), []);

	return (
		<Paper
			elevation={3}
			sx={{
				p: 2,
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Typography
				sx={{
					color: "primary.main",
					fontWeight: 600,
					fontSize: "0.85rem",
					mb: 1,
				}}
			>
				Turn History{!loading && rows.length > 0 ? ` (${rows.length})` : ""}
			</Typography>
			{/* Fills the column instead of growing with the rows: the point of the split is
			    that the whole screen lands in one view. */}
			<div ref={containerRef} style={{ width: "100%", flex: 1, minHeight: 0 }}>
				<AgGridReact
					rowData={loading ? undefined : rows}
					rowModelType="clientSide"
					columnDefs={columnDefs}
					defaultColDef={{ resizable: true, sortable: true }}
					headerHeight={HEADER_HEIGHT}
					rowHeight={ROW_HEIGHT}
					enableBrowserTooltips
					suppressContextMenu
					suppressCellFocus
					// Reserve the vertical scrollbar from the start. Otherwise it appears once
					// the rows land, takes 11px, and the columns no longer fit across.
					alwaysShowVerticalScroll
					overlayNoRowsTemplate={NO_ROWS_OVERLAY}
					overlayLoadingTemplate={LOADING_OVERLAY}
					loading={loading}
					// Refit once the rows are in: the vertical scrollbar only shows up then,
					// and it takes width the first fit had not accounted for.
					onFirstDataRendered={() => apiRef.current?.sizeColumnsToFit()}
					onRowDataUpdated={() => apiRef.current?.sizeColumnsToFit()}
					onGridReady={(event: GridReadyEvent) => {
						registerApi(event.api);
						onGridApi?.(event.api);
					}}
				/>
			</div>
		</Paper>
	);
};
